import { debounceTime, FooterButtons } from '@fl/cmsch-fe-library';
import { debounce } from 'lodash/fp';
import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tCommon, useOurTranslation } from 'app/translations';
import { formHelpers, useForm } from 'utils/forms';
import { validate } from './validate';
import { adminAction } from '../../model';
import { adminDiscriminatorOptionsSelector, simpleAdminSelector } from '../../model/selector';
import { roleFormName, roleInitialValues } from '../../types';
import { CreateOrEditMode } from '../../types/permission-form-mode';
interface Props {
  /** Role id when editing, or `null` for create mode. */
  roleId: number | null;
  /** Create or edit mode. */
  mode: CreateOrEditMode;
  /** Called when the form is submitted. */
  onSubmit(): void;
  /** Called when the form is cancelled. */
  onCancel(): void;
}
const RoleFormBase: FC<Props> = ({
  onSubmit,
  onCancel,
  mode,
  roleId
}) => {
  const tableData = useSelector(simpleAdminSelector.roleDetailsTableData);
  const discriminatorOptions = useSelector(adminDiscriminatorOptionsSelector);
  const dispatch = useDispatch();
  const {
    t
  } = useOurTranslation('admin');
  const {
    Form,
    Fields,
    submitting,
    valid,
    pristine,
    asyncValidating
  } = useForm({
    form: roleFormName,
    initialValues: roleInitialValues,
    destroyOnUnmount: true,
    onSubmit,
    validate
  });
  const isEdit = mode === 'edit' && roleId !== null;
  const initFormData = useMemo(() => tableData.filter(role => role.id === roleId)[0], []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEdit && initFormData) dispatch(formHelpers.initialize(roleFormName, initFormData));
  }, [dispatch, initFormData, isEdit, tableData]);
  const validateData = useCallback(() => {
    dispatch(adminAction.validateRole(initFormData, mode));
  }, [dispatch, initFormData, mode]);
  const validateDebounced = useMemo(() => debounce(debounceTime)(validateData), [validateData]);
  return <Form data-sentry-element="Form" data-sentry-component="RoleFormBase" data-sentry-source-file="index.tsx">
            <Fields.Input name="code" type="text" label={t('code')} disabled={submitting} onFieldChange={validateDebounced} autoFocus isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.Input name="name" type="text" label={t('name')} disabled={submitting} onFieldChange={validateDebounced} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.NumberSelect name="discriminatorId" label={t('discriminator')} options={discriminatorOptions} disabled={submitting} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.Checkbox name="userEnable" label={t('userEnableRole')} disabled={submitting} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <FooterButtons submitting={submitting} valid={valid} pristine={pristine} withoutDivider onCancel={onCancel} asyncValidating={asyncValidating} submitTitle={isEdit ? tCommon('save') : tCommon('create')} data-sentry-element="FooterButtons" data-sentry-source-file="index.tsx" />
        </Form>;
};

/**
 * Admin role form.
 *
 * @example
 * <RoleForm
 *   roleId={123}
 *   mode="edit"
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
export const RoleForm = memo(RoleFormBase);